/**
 * SearchPicker Component
 * Autocomplete search picker for cleaners, customers, etc.
 */

import { api } from '../utils/api.js';
import { debounce, escapeHtml } from '../utils/helpers.js';

/**
 * SearchPicker class for autocomplete search inputs
 */
export class SearchPicker {
  /**
   * Create a SearchPicker instance
   * @param {object} options - Configuration options
   * @param {string} options.inputId - ID of the search input
   * @param {string} options.dropdownId - ID of the dropdown container
   * @param {string} options.hiddenInputId - ID of hidden input for selected value
   * @param {string} options.endpoint - API endpoint for search
   * @param {number} options.minChars - Minimum characters to trigger search (default: 2)
   * @param {number} options.debounceMs - Debounce time in ms (default: 250,
   *   matching the job-form customer picker for consistent typing feel)
   * @param {number} options.maxResults - Maximum results to show (default: 8)
   * @param {Function} options.renderItem - Custom item renderer
   * @param {Function} options.isItemDisabled - Optional ``(item) => boolean``.
   *   Disabled items render at reduced opacity, are skipped by keyboard
   *   navigation, and cannot be selected by mouse. Use to surface
   *   ineligible options (e.g. paused customers in creation pickers)
   *   while keeping them VISIBLE so the operator understands why a name
   *   does not appear in the active list.
   * @param {Function} options.onSelect - Callback when item is selected
   * @param {Function} options.onClear - Callback when selection is cleared
   * @param {string} options.valueKey - Key for the value (default: 'id')
   * @param {string} options.labelKey - Key for the display label (default: 'name')
   * @param {string} options.placeholder - Placeholder text
   */
  constructor(options) {
    this.inputId = options.inputId;
    this.dropdownId = options.dropdownId;
    this.hiddenInputId = options.hiddenInputId;
    this.clearBtnId = options.clearBtnId;
    this.endpoint = options.endpoint;
    this.minChars = options.minChars != null ? options.minChars : 2;
    this.debounceMs = options.debounceMs || 250;
    this.maxResults = options.maxResults || 8;
    this.renderItem = options.renderItem;
    this.isItemDisabled = options.isItemDisabled || (() => false);
    this.onSelect = options.onSelect;
    this.onClear = options.onClear;
    this.valueKey = options.valueKey || 'id';
    this.labelKey = options.labelKey || 'name';
    this.placeholder = options.placeholder || 'Search...';

    // State
    this.selectedValue = null;
    this.selectedItem = null;
    this.isOpen = false;
    this.results = [];
    this.highlightedIndex = -1;
    // Monotonic search counter — a slow earlier response is dropped so
    // it can't overwrite a newer one (out-of-order guard).
    this._searchSeq = 0;

    // Elements
    this.input = document.getElementById(this.inputId);
    this.dropdown = document.getElementById(this.dropdownId);
    this.hiddenInput = this.hiddenInputId ? document.getElementById(this.hiddenInputId) : null;
    this.clearBtn = this.clearBtnId ? document.getElementById(this.clearBtnId) : null;

    // Create dropdown if not exists
    if (!this.dropdown && this.input) {
      this.createDropdown();
    }

    // Create clear button if not exists but we have an input
    if (!this.clearBtn && this.input) {
      this.createClearButton();
    }

    // Bind methods. Keep an UNDEBOUNCED reference (``_searchNow``) so the
    // empty-query open (focus / cleared input) fires IMMEDIATELY, like the
    // job-form picker — only keystrokes go through the debounce. Without
    // this the initial open waited a full ``debounceMs`` before even
    // hitting the API, which made the list filters feel noticeably slower
    // than the mission form on click.
    this._searchNow = this.search.bind(this);
    this.search = debounce(this._searchNow, this.debounceMs);
    this.handleInput = this.handleInput.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.selectItem = this.selectItem.bind(this);
    this.clear = this.clear.bind(this);

    // Initialize
    this.bindEvents();

    // Show clear button if there's an initial value
    this.updateClearButtonVisibility();
  }

  /**
   * Create dropdown element
   */
  createDropdown() {
    this.dropdown = document.createElement('div');
    this.dropdown.id = this.dropdownId || `${this.inputId}-dropdown`;
    this.dropdown.className =
      'absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg hidden max-h-60 overflow-y-auto';

    // Position relative to input
    const wrapper = this.input.parentElement;
    if (!wrapper.classList.contains('relative')) {
      wrapper.classList.add('relative');
    }
    wrapper.appendChild(this.dropdown);
  }

  /**
   * Create clear button element. Style mirrors the canonical clear-X
   * used by the job / quote / worksite forms (rounded grey hover bg,
   * stroke-based ×) so every input clear control across the app looks
   * and feels the same.
   */
  createClearButton() {
    this.clearBtn = document.createElement('button');
    this.clearBtn.type = 'button';
    this.clearBtn.id = this.clearBtnId || `${this.inputId}-clear-btn`;
    this.clearBtn.className =
      'hidden absolute inset-y-0 right-2 flex items-center justify-center w-6 h-6 my-auto rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-700 focus:outline-none';
    this.clearBtn.innerHTML = `
      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
      </svg>
    `;

    // Position relative to input
    const wrapper = this.input.parentElement;
    if (!wrapper.classList.contains('relative')) {
      wrapper.classList.add('relative');
    }
    wrapper.appendChild(this.clearBtn);
  }

  /**
   * Update clear button visibility based on input value
   */
  updateClearButtonVisibility() {
    if (!this.clearBtn) return;

    if (this.input.value.trim() || (this.hiddenInput && this.hiddenInput.value)) {
      this.clearBtn.classList.remove('hidden');
    } else {
      this.clearBtn.classList.add('hidden');
    }
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    if (!this.input) return;

    this.input.addEventListener('input', this.handleInput);
    this.input.addEventListener('keydown', this.handleKeyDown);
    this.input.addEventListener('blur', this.handleBlur);
    this.input.addEventListener('focus', () => {
      if (this.results.length > 0 && this.input.value.length >= this.minChars) {
        this.showDropdown();
      } else if (this.minChars === 0 && !this.isOpen) {
        // Open instantly — bypass the keystroke debounce.
        this._searchNow(this.input.value.trim());
      }
    });

    // Bind clear button
    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.clear();
      });
    }

    // Click outside to close
    document.addEventListener('click', (e) => {
      if (!this.input.contains(e.target) && !this.dropdown?.contains(e.target)) {
        this.hideDropdown();
      }
    });
  }

  /**
   * Handle input changes
   */
  handleInput(e) {
    const query = e.target.value.trim();

    // Clear selection if input changed
    if (this.selectedValue !== null) {
      this.selectedValue = null;
      this.selectedItem = null;
      if (this.hiddenInput) {
        this.hiddenInput.value = '';
      }
    }

    if (query.length >= this.minChars) {
      if (query.length === 0) {
        // Cleared input (minChars 0): refresh the top results instantly,
        // matching the open. Only actual keystrokes are debounced.
        this._searchNow(query);
      } else {
        this.search(query);
      }
    } else {
      this.hideDropdown();
      this.results = [];
    }
  }

  /**
   * Handle keyboard navigation
   */
  handleKeyDown(e) {
    if (!this.isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.highlightedIndex = this._nextEnabledIndex(this.highlightedIndex, +1);
        this.updateHighlight();
        break;

      case 'ArrowUp':
        e.preventDefault();
        this.highlightedIndex = this._nextEnabledIndex(this.highlightedIndex, -1);
        this.updateHighlight();
        break;

      case 'Enter':
        e.preventDefault();
        if (
          this.highlightedIndex >= 0
          && this.results[this.highlightedIndex]
          && !this.isItemDisabled(this.results[this.highlightedIndex])
        ) {
          this.selectItem(this.results[this.highlightedIndex]);
        }
        break;

      case 'Escape':
        this.hideDropdown();
        break;
    }
  }

  /**
   * Walk ``this.results`` from ``current`` in ``direction`` (+1 / -1)
   * and return the first index whose item is not disabled. Returns
   * the existing ``current`` if no enabled item is reachable in that
   * direction (so the highlight does not jump to a disabled row).
   */
  _nextEnabledIndex(current, direction) {
    if (!this.results.length) return -1;
    let i = current + direction;
    while (i >= 0 && i < this.results.length) {
      if (!this.isItemDisabled(this.results[i])) return i;
      i += direction;
    }
    return current;
  }

  /**
   * Handle blur event
   */
  handleBlur() {
    // Delay to allow click on dropdown items
    setTimeout(() => {
      this.hideDropdown();
    }, 200);
  }

  /**
   * Search for items
   */
  async search(query) {
    const seq = ++this._searchSeq;

    // Anti-blink: paint the spinner ONLY on the first open (when no
    // result row is rendered yet). While typing / re-searching we keep
    // the previous results visible until the new ones arrive, so the
    // dropdown swaps content in place instead of flashing spinner →
    // results on every keystroke / fast response. Opening it now also
    // gives immediate feedback that the click/keystroke registered
    // instead of leaving a dead gap until the response lands.
    if (this.dropdown && !this.dropdown.querySelector('.search-picker-item')) {
      this.dropdown.innerHTML =
        '<div class="p-3 text-center">'
        + '<svg class="w-5 h-5 mx-auto animate-spin text-gray-300 dark:text-gray-500" fill="none" viewBox="0 0 24 24">'
        + '<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>'
        + '<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>'
        + '</svg></div>';
      this.showDropdown();
    }

    try {
      // ``this.endpoint`` may already carry a query string (e.g. the
      // customer pickers append ``?include=all`` so paused customers
      // stay searchable in filter mode). Use ``&`` instead of ``?`` in
      // that case — otherwise the resulting URL ``…?include=all?q=foo``
      // is invalid and the server-side parser silently drops both
      // ``q`` and ``limit`` (and treats ``include`` as the literal
      // ``all?q=foo``), making the dropdown look like the search
      // is broken on every page that uses an opt-in scope.
      const sep = this.endpoint.includes('?') ? '&' : '?';
      const url = `${this.endpoint}${sep}q=${encodeURIComponent(query)}&limit=${this.maxResults}`;
      const data = await api(url, { method: 'GET' }, { showErrorToast: false });
      if (seq !== this._searchSeq) return;  // a newer search superseded this one

      // Handle different response formats
      this.results = Array.isArray(data) ? data : data.results || data.items || [];

      this.renderResults();
      this.showDropdown();
    } catch (error) {
      if (seq !== this._searchSeq) return;
      console.error('Search error:', error);
      this.results = [];
      this.hideDropdown();
    }
  }

  /**
   * Render search results
   */
  renderResults() {
    if (!this.dropdown) return;

    if (this.results.length === 0) {
      this.dropdown.innerHTML = `
        <div class="p-3 text-sm text-gray-500 dark:text-gray-400">
          No results found
        </div>
      `;
      return;
    }

    this.dropdown.innerHTML = this.results
      .map((item, index) => {
        const html = this.renderItem
          ? this.renderItem(item, index)
          : this.defaultRenderItem(item, index);
        const disabled = this.isItemDisabled(item);
        // Disabled items keep ``data-index`` so callers can still
        // inspect them, but get a different class set: no hover, no
        // pointer cursor, lower opacity. We use ``cursor-default``
        // (the standard arrow) rather than ``cursor-not-allowed``
        // (the "forbidden" cursor) — the badge + tooltip already
        // signal the disabled state, and the prohibition cursor
        // reads as too aggressive for a UX hint. Click handler is
        // skipped unconditionally below.
        const classes = disabled
          ? 'search-picker-item search-picker-item-disabled cursor-default opacity-60'
          : `search-picker-item cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
              index === this.highlightedIndex ? 'bg-gray-100 dark:bg-gray-700' : ''
            }`;
        return `
          <div class="${classes}" data-index="${index}" ${disabled ? 'data-disabled="true"' : ''}>
            ${html}
          </div>
        `;
      })
      .join('');

    // Use mousedown + preventDefault to select before blur hides the
    // dropdown. Disabled items are intentionally skipped: the operator
    // sees why the item is not pickable (badge + tooltip rendered by
    // the consumer's ``renderItem``) but cannot accidentally select it.
    this.dropdown.querySelectorAll('.search-picker-item').forEach((el) => {
      if (el.dataset.disabled === 'true') return;
      el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        const index = parseInt(el.dataset.index, 10);
        this.selectItem(this.results[index]);
      });
    });
  }

  /**
   * Default item renderer
   */
  defaultRenderItem(item, _index) {
    const label = item[this.labelKey] || item.name || item.title || 'Unknown';
    const photo = item.photo_url || item.avatar_url || item.image;

    if (photo) {
      return `
        <div class="flex items-center gap-3 p-2">
          <img src="${escapeHtml(photo)}" alt="" class="w-8 h-8 rounded-full object-cover">
          <span class="text-sm text-gray-900 dark:text-white">${escapeHtml(label)}</span>
        </div>
      `;
    }

    return `
      <div class="p-2">
        <span class="text-sm text-gray-900 dark:text-white">${escapeHtml(label)}</span>
      </div>
    `;
  }

  /**
   * Update highlighted item styling
   */
  updateHighlight() {
    if (!this.dropdown) return;

    this.dropdown.querySelectorAll('.search-picker-item').forEach((el, index) => {
      if (index === this.highlightedIndex) {
        el.classList.add('bg-gray-100', 'dark:bg-gray-700');
        el.scrollIntoView({ block: 'nearest' });
      } else {
        el.classList.remove('bg-gray-100', 'dark:bg-gray-700');
      }
    });
  }

  /**
   * Select an item
   */
  selectItem(item) {
    this.selectedValue = item[this.valueKey];
    this.selectedItem = item;

    // Update visible input
    const label = item[this.labelKey] || item.name || item.title;
    this.input.value = label;

    // Update hidden input
    if (this.hiddenInput) {
      this.hiddenInput.value = this.selectedValue;
    }

    this.hideDropdown();

    // Drop the focus ring once a choice is made: the selection is
    // committed (chip / hidden input set), so keeping the caret blinking
    // in the field reads as "still searching" and looks unfinished.
    if (this.input) {
      this.input.blur();
    }

    // Show clear button
    this.updateClearButtonVisibility();

    // Callback
    if (this.onSelect) {
      this.onSelect(item, this);
    }
  }

  /**
   * Clear selection
   */
  clear() {
    this.selectedValue = null;
    this.selectedItem = null;
    this.input.value = '';

    if (this.hiddenInput) {
      this.hiddenInput.value = '';
    }

    this.results = [];
    this.hideDropdown();

    // Hide clear button
    this.updateClearButtonVisibility();

    if (this.onClear) {
      this.onClear(this);
    }
  }

  /**
   * Show dropdown
   */
  showDropdown() {
    if (!this.dropdown) return;

    this.dropdown.classList.remove('hidden');
    this.isOpen = true;
    this.highlightedIndex = -1;
  }

  /**
   * Hide dropdown
   */
  hideDropdown() {
    if (!this.dropdown) return;

    this.dropdown.classList.add('hidden');
    this.isOpen = false;
    this.highlightedIndex = -1;
  }

  /**
   * Get selected value
   */
  getValue() {
    return this.selectedValue;
  }

  /**
   * Get selected item
   */
  getSelectedItem() {
    return this.selectedItem;
  }

  /**
   * Set value programmatically
   */
  setValue(value, label = null) {
    this.selectedValue = value;

    if (this.hiddenInput) {
      this.hiddenInput.value = value || '';
    }

    if (this.input && label) {
      this.input.value = label;
    }
  }

  /**
   * Destroy the picker instance
   */
  destroy() {
    this.input = null;
    this.dropdown = null;
    this.hiddenInput = null;
  }
}

// Expose globally for backward compatibility
window.SearchPicker = SearchPicker;

export default SearchPicker;
